/**
 * Tenant service for tenant management
 */

import type { QueryParams } from '@type/services';
import { BaseService } from './BaseService';

export class TenantService extends BaseService {
  /**
   * Create a new tenant
   * @param tenantData - Tenant data
   */
  async create(tenantData: Record<string, unknown>) {
    return this.request<void>({
      method: 'POST',
      url: this.TENANT,
      data: tenantData,
    });
  }

  /**
   * Get all tenants
   * @param params - Query parameters
   * @returns List of tenants
   */
  async findAll(params?: QueryParams) {
    return this.request<
      {
        tenantId: string;
        tenantName: string;
      }[]
    >({
      method: 'GET',
      url: this.TENANT,
      params,
    });
  }

  /**
   * Register a new tenant to the application
   * @param tenantData - Tenant registration data
   * @returns Registration response with access token
   */
  async registerTenant(tenantData: Record<string, unknown>) {
    return this.request<Record<string, unknown>>({
      method: 'POST',
      url: `${this.TENANT}/register-tenant`,
      data: tenantData,
    });
  }

  /**
   * Update a tenant
   * @param id - Tenant ID
   * @param tenantData - Tenant update data
   */
  async updateTenant(id: string, tenantData: Record<string, unknown>) {
    return this.request<void>({
      method: 'PATCH',
      url: `${this.TENANT}/${id}`,
      data: tenantData,
    });
  }

  /**
   * Get all access tokens
   * @param params - Query parameters
   * @returns List of access tokens
   */
  async getAccessTokens(params?: QueryParams) {
    return this.request<Record<string, unknown>[]>({
      method: 'GET',
      url: `${this.TENANT}/get-access-tokens`,
      params,
    });
  }

  /**
   * Fetch history for the tenant
   * @param tenantId - Tenant ID
   * @returns Tenant history
   */
  async fetchHistory(tenantId: string) {
    return this.request<Record<string, unknown>[]>({
      method: 'GET',
      url: `${this.TENANT}/${tenantId}/fetch-history`,
    });
  }

  /**
   * Get tenants for a user (public - for login page)
   * @param username - Username
   * @returns List of tenants accessible by the user
   */
  async getUserTenantsPublic(username: string) {
    return this.request<{ tenantId: string; tenantName: string }[]>({
      method: 'GET',
      url: `${this.TENANT}/user-tenants/${username}`,
    });
  }

  /**
   * Get all users mapped to a tenant
   * @param tenantId - Tenant ID
   * @returns List of user-tenant mappings
   */
  async getTenantUsers(tenantId: string) {
    return this.request<Record<string, unknown>[]>({
      method: 'GET',
      url: `${this.TENANT}/${tenantId}/users`,
    });
  }

  /**
   * Create user-tenant mapping (admin only)
   * @param mappingData - Mapping data
   */
  async createMapping(mappingData: Record<string, unknown>) {
    return this.request<void>({
      method: 'POST',
      url: `${this.TENANT}/mapping`,
      data: mappingData,
    });
  }

  /**
   * Delete user-tenant mapping (admin only)
   * @param username - Username
   * @param tenantId - Tenant ID
   */
  async deleteMapping(username: string, tenantId: string) {
    return this.request<void>({
      method: 'DELETE',
      url: `${this.TENANT}/mapping/${username}/${tenantId}`,
    });
  }

  /**
   * Delete a tenant
   * @param tenantId - Tenant ID
   */
  async delete(tenantId: string) {
    return this.request<void>({
      method: 'DELETE',
      url: `${this.TENANT}/${tenantId}`,
    });
  }

  /**
   * Create api users for all tenant if not present
   */
  async createApiUsersForAllTenants() {
    return this.request<void>({
      method: 'POST',
      url: `${this.TENANT}/create-api-users-for-all-tenants`,
    });
  }
}
